/**
 * Cart API Discount Handler
 */

class CartAPIDiscountHandler {
  constructor() {
    this.cart = document.querySelector("cart-drawer");
    this.changeEvent = new Event("change", { bubbles: true });
  }

  /**
   * Applies a discount code to the cart using Cart API
   * @param {string} discountCode - The discount code to be applied
   * @returns {Promise<{success: boolean}>} Whether the code is applied to the cart
   */
  async applyDiscountCode(discountCode) {
    try {
      const newCode = discountCode.trim();
      const originalCart = await this.getCart();

      const existingCodes = originalCart.discount_codes
        .map(discount_code => discount_code.code)
        .filter(code => code.toLowerCase() !== newCode.toLowerCase());

      const response = await fetch(`/cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: [...existingCodes, newCode].join(', ')
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || `Error ${response.status}: ${response.statusText}`);
      }

      const currentCart = await response.json();

      const codeApplied = currentCart.discount_codes.some(discount_code =>
        discount_code.applicable &&
        discount_code.code.toLowerCase() === newCode.toLowerCase()
      );

      return { success: codeApplied };

    } catch (error) {
      console.error('Error applying discount code:', error);
      return { success: false };
    }
  }

  /**
   * Removes a discount code from the cart
   * @param {string} discountCode - The discount code to be removed
   * @returns {Promise<{success: boolean}>} Whether the code is off the cart
   */
  async removeDiscountCode(discountCode) {
    try {
      const removedCode = discountCode.trim();
      const originalCart = await this.getCart();

      const remainingCodes = originalCart.discount_codes
        .map(discount_code => discount_code.code)
        .filter(code => code.toLowerCase() !== removedCode.toLowerCase());

      const response = await fetch(`/cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: remainingCodes.join(', ')
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || `Error ${response.status}: ${response.statusText}`);
      }

      const currentCart = await response.json();

      const codeRemoved = !currentCart.discount_codes.some(discount_code =>
        discount_code.code.toLowerCase() === removedCode.toLowerCase()
      );

      return { success: codeRemoved };

    } catch (error) {
      console.error('Error removing discount code:', error);
      return { success: false };
    }
  }

  /**
   * Gets complete cart data
   * @returns {Promise<Object>} Cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Updates the cart interface (minicart or cart page)
   * @returns {Promise<void>}
   */
  async refreshCartInterface() {
    try {
      if (window.location.pathname === window.routes.cart_url) {
        await this.refreshCartPage();
      } else {
        await this.refreshMinicart();
      }
    } catch (error) {
      console.error('Error refreshing cart interface:', error);
    }
  }

  /**
   * Updates the cart page
   * @returns {Promise<void>}
   */
  async refreshCartPage() {
    try {
      const cartPage = await fetch('/cart');

      if (!cartPage.ok) {
        throw new Error("Network response was not ok");
      }

      const parsedCartPage = await cartPage.text();

      const domParsedCartPage = new DOMParser().parseFromString(
        parsedCartPage,
        "text/html"
      );

      const cartItemsSection = document.querySelector(".shopify-section.cart-items");
      if (cartItemsSection) {
        const newCartItemsSection = domParsedCartPage.querySelector(".shopify-section.cart-items");
        if (newCartItemsSection) {
          cartItemsSection.innerHTML = newCartItemsSection.innerHTML;
        }
      }

      const footerWrapper = document.querySelector(".cart__footer-wrapper");
      if (footerWrapper) {
        const newFooterWrapper = domParsedCartPage.querySelector(".cart__footer-wrapper");
        if (newFooterWrapper) {
          footerWrapper.innerHTML = newFooterWrapper.innerHTML;
        }
      }

    } catch (error) {
      console.error('Error refreshing cart page:', error);
    }
  }

  /**
   * Updates the minicart
   * @returns {Promise<void>}
   */
  async refreshMinicart() {
    try {
      const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const parsedResponse = await response.json();

      if (this.cart && this.cart.renderContents) {
        this.cart.renderContents({ sections: parsedResponse });
      }
    } catch (error) {
      console.error('Error refreshing minicart:', error);
    }
  }
}

window.CartAPIDiscountHandler = CartAPIDiscountHandler;